import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Avatar, Loading, inputCls, labelCls, btnPrimary, btnGhost } from "@/components/kit";
import { MapPinArea, BookOpen, ChalkboardTeacher, SignIn, SignOut, Clock, Trash, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const typeMeta = {
  study_hall: { label: "Study Hall", icon: BookOpen, color: "#0A84FF" },
  office_hours: { label: "Office Hours", icon: ChalkboardTeacher, color: "#FF9F0A" },
};

const fmtTime = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const duration = (a, b) => {
  if (!b) return "active";
  const m = Math.round((new Date(b) - new Date(a)) / 60000);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

function CheckinRow({ c, onCheckout, onDelete }) {
  const m = typeMeta[c.type] || typeMeta.study_hall;
  const Icon = m.icon;
  const active = !c.checkout_at;
  return (
    <div className="flex items-center gap-3 bg-[#0A0A0A] border border-white/10 rounded-sm p-4" data-testid={`checkin-${c.id}`}>
      <div className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${m.color}22` }}>
        <Icon size={20} weight="fill" color={m.color} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-white text-sm">
          {m.label}{c.professor_name ? ` · ${c.professor_name}` : ""}
          {active && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#34C759] font-bold">● Active</span>}
        </div>
        <div className="text-xs text-[#737373] flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-1"><Clock size={12} /> {fmtTime(c.checkin_at)}</span>
          <span>· {duration(c.checkin_at, c.checkout_at)}</span>
          {c.note && <span>· {c.note}</span>}
        </div>
      </div>
      {onCheckout && active && <button onClick={() => onCheckout(c.id)} className="text-[#34C759] hover:text-white text-xs uppercase tracking-widest font-bold flex items-center gap-1" data-testid={`checkout-${c.id}`}><SignOut size={16} weight="bold" /> Out</button>}
      {onDelete && <button onClick={() => onDelete(c.id)} className="text-[#737373] hover:text-[#FF3B30]" data-testid={`delete-checkin-${c.id}`}><Trash size={16} weight="bold" /></button>}
    </div>
  );
}

function AthleteCheckins() {
  const [items, setItems] = useState(null);
  const [profs, setProfs] = useState([]);
  const [type, setType] = useState("study_hall");
  const [profId, setProfId] = useState("");
  const [note, setNote] = useState("");

  const load = async () => { const r = await api.get("/checkins"); setItems(r.data); };
  useEffect(() => {
    load();
    api.get("/users", { params: { role: "teacher" } }).then((r) => { setProfs(r.data); if (r.data[0]) setProfId(r.data[0].id); });
  }, []);

  const checkIn = async () => {
    try {
      await api.post("/checkins", { type, professor_id: type === "office_hours" ? profId : null, note: note || null });
      toast.success("Checked in");
      setNote("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not check in"); }
  };
  const doCheckout = async (id) => { await api.post(`/checkins/${id}/checkout`); toast.success("Checked out"); load(); };
  const del = async (id) => { await api.delete(`/checkins/${id}`); toast.success("Removed"); load(); };

  if (!items) return <Loading />;
  const active = items.filter((c) => !c.checkout_at);
  const past = items.filter((c) => c.checkout_at);

  return (
    <div className="px-4 md:px-8 py-8 md:py-12 max-w-4xl mx-auto">
      <PageHeader eyebrow="Study Hall & Office Hours" title="Check" accent="in" icon={SignIn} />

      <Card className="p-6 mb-6">
        <div className="grid grid-cols-2 gap-2 mb-4">
          {Object.entries(typeMeta).map(([k, m]) => (
            <button key={k} onClick={() => setType(k)} data-testid={`checkin-type-${k}`}
              className={`flex items-center justify-center gap-2 py-4 rounded-sm text-sm uppercase tracking-widest font-semibold transition-colors ${type === k ? "text-white" : "bg-[#0A0A0A] border border-white/15 text-[#A3A3A3]"}`}
              style={type === k ? { background: m.color } : {}}>
              <m.icon size={20} weight="fill" /> {m.label}
            </button>
          ))}
        </div>
        {type === "office_hours" && (
          <div className="mb-4">
            <label className={labelCls}>Professor</label>
            <select className={inputCls} value={profId} onChange={(e) => setProfId(e.target.value)} data-testid="checkin-prof-select">
              {profs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}
        <div className="mb-4">
          <label className={labelCls}>Note (optional)</label>
          <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Library, Algebra help" data-testid="checkin-note-input" />
        </div>
        <button onClick={checkIn} className={`${btnPrimary} w-full`} data-testid="checkin-button"><SignIn size={18} weight="bold" /> Check In</button>
      </Card>

      {active.length > 0 && (
        <div className="mb-6">
          <h2 className="font-head text-xl font-bold uppercase tracking-tight mb-3">Active</h2>
          <div className="space-y-3">{active.map((c) => <CheckinRow key={c.id} c={c} onCheckout={doCheckout} onDelete={del} />)}</div>
        </div>
      )}

      <h2 className="font-head text-xl font-bold uppercase tracking-tight mb-3">History</h2>
      {past.length === 0 ? (
        <Card className="p-10 text-center text-[#737373]" data-testid="empty-checkins">No past check-ins yet.</Card>
      ) : (
        <div className="space-y-3" data-testid="checkin-history">{past.map((c) => <CheckinRow key={c.id} c={c} onDelete={del} />)}</div>
      )}
    </div>
  );
}

function StaffCheckins() {
  const [students, setStudents] = useState(null);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/users", { params: { role: "athlete" } }).then((r) => { setStudents(r.data); if (r.data[0]) setSelected(r.data[0].id); });
  }, []);
  useEffect(() => {
    if (!selected) return;
    setItems(null);
    api.get("/checkins", { params: { student_id: selected } }).then((r) => setItems(r.data));
  }, [selected]);

  if (!students) return <Loading />;
  const sel = students.find((s) => s.id === selected);

  return (
    <div className="px-4 md:px-8 py-8 md:py-12 max-w-7xl mx-auto">
      <PageHeader eyebrow="Study Hall & Office Hours" title="Check-in" accent="log" icon={SignIn} />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6 items-start">
        <Card className="p-3 lg:col-span-1">
          <div className="text-[10px] uppercase tracking-widest text-[#737373] font-semibold px-2 py-2">Athletes</div>
          <div className="space-y-1 max-h-[70vh] overflow-y-auto">
            {students.map((s) => (
              <button key={s.id} onClick={() => setSelected(s.id)} data-testid={`checkin-athlete-${s.id}`}
                className={`w-full flex items-center gap-2 px-2 py-2 rounded-sm text-left transition-colors ${selected === s.id ? "bg-[#FF3B30]/15" : "hover:bg-white/5"}`}>
                <Avatar name={s.name} size={32} color="#34C759" />
                <div className="min-w-0"><div className="text-sm text-white truncate">{s.name}</div><div className="text-[11px] text-[#737373]">{s.sport}</div></div>
              </button>
            ))}
          </div>
        </Card>
        <div className="lg:col-span-3">
          {!items ? <Loading /> : (
            <div className="space-y-4">
              {sel && <div className="flex items-center gap-3 mb-2"><Avatar name={sel.name} size={44} color="#34C759" /><div><div className="font-head text-2xl font-bold uppercase tracking-tight">{sel.name}</div><div className="text-xs text-[#737373]">{items.length} check-ins logged</div></div></div>}
              {items.length === 0 ? (
                <Card className="p-10 text-center text-[#737373]">No check-ins for this athlete.</Card>
              ) : (
                <div className="space-y-3" data-testid="staff-checkin-list">{items.map((c) => <CheckinRow key={c.id} c={c} />)}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Checkins() {
  const { user } = useAuth();
  return user?.role === "athlete" ? <AthleteCheckins /> : <StaffCheckins />;
}
